import { Box, Card, Divider, Paper, Typography } from "@mui/material";
import React, { useEffect } from "react";
import theme from "../../theme";
import moment from "moment";
import InfoBoxGroup from "../base/InfoBoxGroup";
import Button from "../base/Button";
import { useDispatch, useSelector } from "../../store";
import { getDownloads } from "../../slices/download";
import { Download, InfoOutlined } from "@mui/icons-material";

interface Props {}

const Downloads: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const { downloads } = useSelector((store) => store.download);
  const latestVersion = `2.4.1`;
  const latestRelease = new Date("March 27, 2025");
  const updates = `improved threat detection algorithms and enhanced dashboard analytics.`;

  useEffect(() => {
    dispatch(getDownloads());
  }, [dispatch]);

  const downloadInfo = downloads.map((dl) => {
    return (
      <Card
        key={dl.id}
        sx={{
          backgroundColor: `${theme.palette.primary.main}25`,
          paddingY: { xs: 5, md: 2 },
          paddingX: { xs: 2, md: 5 },
          borderRadius: 2,
        }}
      >
        <Box
          display={"flex"}
          flexDirection={"row"}
          alignItems={"center"}
          gap={2}
          pb={1}
        >
          <Box
            component="img"
            height="40px"
            src={`/static/images/${
              dl.id === 1
                ? "windows-logo"
                : dl.id === 2
                ? "linux-logo"
                : "docker-logo"
            }.png`}
            alt={`${dl.title} logo`}
          />
          <Box display="flex" flexDirection="column">
            <Box display="flex" flexDirection="row">
              <Typography variant="body1" fontWeight="bold">
                {dl.title}
              </Typography>
            </Box>
            <Box display="flex" flexDirection="row">
              <Typography
                variant={"body1"}
                textAlign={{ xs: "center", md: "left" }}
              >
                {dl.system}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Divider color="black" />
        <Box display="flex" flexDirection="column" py={1} gap={2}>
          <Box display="flex" flexDirection="row">
            <Typography variant="body2" fontWeight="bold">
              {dl.description}
            </Typography>
          </Box>
          {dl.fileName && (
            <Box display="flex" flexDirection="row">
              <Typography
                variant={"subtitle1"}
                textAlign={{ xs: "center", md: "left" }}
                sx={{
                  p: 1,
                  my: 1,
                }}
              >
                {dl.fileName}
                {dl.size ? `(${dl.size} MB)` : ""}
              </Typography>
            </Box>
          )}
          {dl.installation && (
            <Box
              display="flex"
              flexDirection="row"
              sx={{
                p: 1,
                backgroundColor: theme.palette.background.paper,
                borderRadius: "5px",
                my: 1,
              }}
            >
              <Typography
                variant={"subtitle1"}
                textAlign={{ xs: "center", md: "left" }}
              >
                {dl.installation}
              </Typography>
            </Box>
          )}
        </Box>
        <Divider color="black" />
        <Box mt={2} justifySelf={"center"}>
          <Button
            label="Download"
            variant="contained"
            icon={<Download />}
            onClick={() => {}}
          />
        </Box>
      </Card>
    );
  });

  const instructions = [
    {
      id: 1,
      version: "Windows",
      instructions: [
        "Download the Windows installer (.exe) file",
        "Run the installer with administrator privileges",
        "Follow the installation wizard instructions",
        "After installation, launch SVPro Security Hub from the Start menu",
        "Complete the initial setup by providing your license key and configuring your security preferences",
      ],
      systemRequirements: [
        "Windows 10/11 (64-bit)",
        "4GB RAM minimum (8GB recommended)",
        "2GB free disk space",
        ".NET Framework 4.8 or higher",
      ],
      img: "windows-logo",
    },
    {
      id: 2,
      version: "Linux",
      instructions: [
        "Debian/Ubuntu:",
        "CentOS/RHEL:",
        "After installation, start the service:",
      ],
      subInstructions: [
        {
          instructions: [
            `sudo dpkg -i svpro-security-hub-${latestVersion}.deb`,
            "sudo apt-get install -f",
          ],
        },
        {
          instructions: [
            `sudo rpm -ivh svpro-security-hub-${latestVersion}.rpm`,
          ],
        },
        {
          instructions: [
            `sudo systemctl enable svpro-security-hub`,
            "sub systemctl start svpro-security-hub",
          ],
        },
      ],
      systemRequirements: [
        "Ubuntu 20.04+, CentOS 8+, or RHEL 8+",
        "2GB RAM minimum (4GB recommended)",
        "1GB free disk space",
        "systemd",
      ],
      img: "linux-logo",
    },
  ];

  //alertTitle
  const instructionDisplay = instructions.map((ins) => {
    return (
      <Box mb={2}>
        <Box
          display={"flex"}
          flexDirection={"row"}
          alignItems={"center"}
          gap={2}
          sx={{ mb: 2 }}
        >
          <Box
            component="img"
            height="40px"
            src={`/static/images/${ins.img}.png`}
            alt={`${ins.version} logo`}
          />
          <Typography variant="h4" fontWeight={"bold"}>
            {ins.version} Installation
          </Typography>
        </Box>
        <Card
          key={ins.id}
          sx={{
            backgroundColor: `${theme.palette.primary.main}25`,
            paddingY: { xs: 5, md: 2 },
            paddingX: { xs: 2, md: 5 },
            borderRadius: 2,
          }}
        >
          <Box sx={{ pb: 2 }}>
            {ins.instructions.map((i, index) => (
              <Box sx={{ mb: 2, ml: 2 }}>
                <Typography>{i}</Typography>
                {ins.subInstructions && (
                  <Box
                    sx={{
                      p: 1,
                      backgroundColor: theme.palette.background.paper,
                      borderRadius: "5px",
                      my: 1,
                    }}
                  >
                    {ins.subInstructions[index].instructions.map((si) => (
                      <Typography variant="subtitle1">{si}</Typography>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
          <Typography sx={{ mb: 2 }}>System Requirements:</Typography>
          {ins.systemRequirements.map((req) => (
            <Typography sx={{ ml: 2 }}>{req}</Typography>
          ))}
          <Box sx={{ mt: 2 }}>
            <Button
              label={`View detailed ${ins.version} installation guide`}
              onClick={() => {}}
            />
          </Box>
        </Card>
      </Box>
    );
  });

  return (
    <Box>
      <Paper
        sx={{
          backgroundColor: theme.palette.success.light,
          paddingY: "10px",
          paddingX: "16px",
          marginBottom: 5,
        }}
      >
        <Box display={"flex"} flexDirection={"row"}>
          <InfoOutlined color="success" sx={{ marginRight: "12px" }} />
          <Box display={"flex"} flexDirection={"column"} gap={1}>
            <Typography>Latest Version: {latestVersion}</Typography>
            <Typography>
              Released on {moment(latestRelease).format("MMMM DD, YYYY")} with{" "}
              {updates}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ justifySelf: "flex-end" }}>
          <Button onClick={() => {}} label="View Changelog" />
        </Box>
      </Paper>
      <InfoBoxGroup content={downloadInfo} />
      <Typography variant="h3" fontWeight={"bold"} sx={{ mb: 2 }}>
        Installation Instructions
      </Typography>
      <InfoBoxGroup content={instructionDisplay} rows />
    </Box>
  );
};

export default Downloads;
